"use client";
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { useTheme } from '../utils/theming/useTheme.js';
import { wrapper, rail, circle } from './styles.js';
import { ThemeProvider } from 'styled-components';
import { useEventCallback } from '../utils/hooks/useEventCallback.js';
import { useSmartControlled } from '../utils/hooks/useSmartControlled.js';
import { Popover } from '../Popover/Popover.web.js';
import { ContentText } from '../ContentText/ContentText.web.js';
import { spacing } from '../constants/spacing.js';
import { valueToPercentage, clamp, roundToNearestStep } from './utils.common.js';
import { getOwnerDocument, getNewValue } from './utils.web.js';
import { Box } from '../Box/Box.web.js';
import { usePopover } from '../Popover/utils.web.js';

var Slider = function Slider(_ref) {
  var color = _ref.color,
    _ref$max = _ref.max,
    max = _ref$max === undefined ? 100 : _ref$max,
    _ref$min = _ref.min,
    min = _ref$min === undefined ? 0 : _ref$min,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === undefined ? false : _ref$disabled,
    onChangeProp = _ref.onChange,
    valueProp = _ref.value,
    _ref$step = _ref.step,
    step = _ref$step === undefined ? 1 : _ref$step,
    _ref$precision = _ref.precision,
    precision = _ref$precision === undefined ? 0 : _ref$precision,
    _ref$width = _ref.width,
    width = _ref$width === undefined ? '100%' : _ref$width,
    _ref$unit = _ref.unit,
    unit = _ref$unit === undefined ? '' : _ref$unit,
    ariaLabel = _ref.ariaLabel,
    ariaLabelledby = _ref.ariaLabelledby,
    ariaValuetextProp = _ref.ariaValuetext,
    id = _ref.id,
    _ref$showLabels = _ref.showLabels,
    showLabels = _ref$showLabels === undefined ? true : _ref$showLabels,
    _ref$labelPosition = _ref.labelPosition,
    labelPosition = _ref$labelPosition === undefined ? 'top' : _ref$labelPosition;
  var theme = useTheme();
  var sliderRef = React.useRef(null);
  var circleRef = React.useRef(null);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    dragInProgress = _React$useState2[0],
    setDragInProgress = _React$useState2[1];
  var _usePopover = usePopover(),
    popoverOpen = _usePopover.open,
    handlePopoverOpen = _usePopover.handleOpen,
    handlePopoverClose = _usePopover.handleClose;
  var popoverUpdate = React.useRef();
  var _useSmartControlled = useSmartControlled(0, valueProp, onChangeProp),
    _useSmartControlled2 = _slicedToArray(_useSmartControlled, 2),
    valueState = _useSmartControlled2[0],
    onChange = _useSmartControlled2[1];
  var ariaValuetext = React.useCallback(function (value) {
    if (!ariaValuetextProp) {
      return "".concat(value, " ").concat(unit);
    }
    return typeof ariaValuetextProp === 'function' ? ariaValuetextProp(value) : ariaValuetextProp;
  }, [unit, ariaValuetextProp]);
  var handleChange = function handleChange(newValue) {
    // istanbul ignore next - sanity
    if (newValue != null) {
      onChange(newValue);
    }
  };
  var handleMove = useEventCallback(function (event) {
    var pos;
    if (event.clientX) {
      pos = {
        x: event.clientX,
        y: event.clientY
      };
    } else {
      pos = {
        x: event.changedTouches[0].clientX,
        y: event.changedTouches[0].clientY
      };
    }
    var newValue = getNewValue(sliderRef, pos, min, max, step, precision);
    handleChange(newValue);
  });
  var handleMouseUp = useEventCallback(function (_event) {
    setDragInProgress(false);
    handlePopoverClose();
    removeListeners();
  });
  var handleTouchStart = useEventCallback(function (event) {
    event.preventDefault();
    setDragInProgress(true);
    handlePopoverOpen(event);
    var pos = {
      x: event.changedTouches[0].clientX,
      y: event.changedTouches[0].clientY
    };
    var newValue = getNewValue(sliderRef, pos, min, max, step, precision);
    handleChange(newValue);
    var doc = getOwnerDocument(sliderRef);
    doc.addEventListener('touchmove', handleMove);
    doc.addEventListener('touchend', handleMouseUp);
  });
  var handleMouseDown = useEventCallback(function (event) {
    // Only handle left clicks
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    setDragInProgress(true);
    handlePopoverOpen(event);
    var pos = {
      x: event.clientX,
      y: event.clientY
    };
    var newValue = getNewValue(sliderRef, pos, min, max, step, precision);
    handleChange(newValue);
    var doc = getOwnerDocument(sliderRef);
    doc.addEventListener('mousemove', handleMove);
    doc.addEventListener('mouseup', handleMouseUp);
  });
  var removeListeners = React.useCallback(function () {
    var doc = getOwnerDocument(sliderRef);
    doc.removeEventListener('mousemove', handleMove);
    doc.removeEventListener('mouseup', handleMouseUp);
    doc.removeEventListener('touchmove', handleMove);
    doc.removeEventListener('touchend', handleMouseUp);
  }, [handleMove, handleMouseUp]);
  React.useEffect(function () {
    if (disabled) {
      removeListeners();
    }
  }, [disabled, removeListeners]);
  React.useEffect(function () {
    if (disabled) return;
    var current = sliderRef.current;
    // istanbul ignore next
    if (!current) {
      return;
    }
    current.addEventListener('touchstart', handleTouchStart);
    return function () {
      current.removeEventListener('touchstart', handleTouchStart);
      removeListeners();
    };
  }, [sliderRef, handleMouseUp, handleMove, handleTouchStart, removeListeners, disabled]);

  // istanbul ignore next
  React.useEffect(function () {
    var _popoverUpdate$curren;
    if ((_popoverUpdate$curren = popoverUpdate.current) !== null && _popoverUpdate$curren !== undefined && _popoverUpdate$curren.update) {
      popoverUpdate.current.update();
    }
  }, [popoverUpdate, valueState]);
  var handlePopoverConditionalOpen = React.useCallback(function (event) {
    // istanbul ignore next -- this just cancels reopen
    if (dragInProgress) return;
    handlePopoverOpen(event);
  }, [dragInProgress, handlePopoverOpen]);
  var handlePopoverConditionalClose = React.useCallback(function () {
    if (dragInProgress) return;
    handlePopoverClose();
  }, [dragInProgress, handlePopoverClose]);
  var handleKeyDown = useEventCallback(function (event) {
    var bigStep = (max - min) / 10;
    var smallSTep = (max - min) / 20;
    var newValue;
    switch (event.key) {
      case 'Home':
        newValue = min;
        break;
      case 'End':
        newValue = max;
        break;
      case 'PageUp':
        newValue = valueState + bigStep;
        break;
      case 'PageDown':
        newValue = valueState - bigStep;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        newValue = valueState + smallSTep;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        newValue = valueState - smallSTep;
        break;
      default:
        return;
    }

    // Prevent scroll of the page
    event.preventDefault();
    newValue = clamp(roundToNearestStep(newValue, min, step, precision), min, max);
    onChange(newValue);
  });
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(wrapper, {
    id: id,
    "data-testid": "elemental-Slider-".concat(id || valueState, "-wrapper"),
    width: width,
    ref: sliderRef,
    role: "slider",
    $disabled: disabled,
    $labelPosition: labelPosition,
    onMouseDown: disabled ? undefined : handleMouseDown,
    tabIndex: disabled ? undefined : 0,
    onKeyDown: disabled ? undefined : handleKeyDown,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-valuemax": max,
    "aria-valuemin": min,
    "aria-valuenow": valueState,
    "aria-valuetext": ariaValuetext(valueState),
    onFocus: handlePopoverConditionalOpen,
    onBlur: handlePopoverConditionalClose
  }, showLabels && /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: labelPosition === 'bottom' ? spacing.xxxs : undefined,
    marginBottom: labelPosition === 'top' ? spacing.xxxs : undefined
  }, /*#__PURE__*/React.createElement(ContentText, {
    color: theme.slider.web.label.color,
    size: "s"
  }, "".concat(min, " ").concat(unit)), /*#__PURE__*/React.createElement(ContentText, {
    color: theme.slider.web.label.color,
    size: "s"
  }, "".concat(max, " ").concat(unit))), /*#__PURE__*/React.createElement(Box, {
    position: "relative",
    width: "100%",
    minHeight: theme.slider.web.height,
    display: "flex",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(rail, {
    color: theme.slider.web.rail.inactive,
    disabled: disabled
  }), /*#__PURE__*/React.createElement(rail, {
    color: color || theme.slider.web.color,
    style: {
      width: valueToPercentage(valueState, min, max) + '%'
    },
    disabled: disabled
  }), /*#__PURE__*/React.createElement(circle, {
    "data-testid": "elemental-Slider-".concat(id || valueState, "-circle"),
    disabled: disabled,
    style: {
      left: valueToPercentage(valueState, min, max) + '%'
    },
    color: color || theme.slider.web.color,
    ref: circleRef,
    onFocus: handlePopoverConditionalOpen,
    onBlur: handlePopoverConditionalClose,
    onMouseEnter: handlePopoverConditionalOpen,
    onMouseLeave: handlePopoverConditionalClose
  })), /*#__PURE__*/React.createElement(Popover, {
    opened: popoverOpen,
    anchor: circleRef.current,
    anchorPos: "top",
    backgroundColor: theme.slider.web.valueIndicator.background,
    popperOffset: 18,
    padding: 0
  }, /*#__PURE__*/React.createElement(ContentText, {
    p: spacing.xxs,
    color: theme.slider.web.valueIndicator.color
  }, "".concat(valueState, " ").concat(unit)))));
};

export { Slider };
